/**
 * A leitura de todos os parâmetros numa profundidade (spec 07 §6.1, item 4; a prancheta 1, «Na
 * profundidade 11,80 m»): a zona SBTn escrita junto da cor, e os grupos Medidos · Corrigidos e
 * normalizados · Classificação · Correlações, cada linha com a marca de homologação (R7) e, numa
 * simulação, o antes e o depois («16,16 → 12,57 kPa»). A conta é a de `leituraNaCota` (o índice do bruto
 * acha a linha do resultado e a do antes, cada uma pelo seu mapa). O selo da zona leva a marca do SBTn
 * (`marcaDaZona`). O Portal passa `mostrarNaoHomologadas` falso: só o homologado aparece (e o selo da zona
 * some quando o SBTn não é homologado). Só o título da profundidade e a zona são anunciados (`aria-live`).
 */
import type { JSX } from 'react';
import type { Leituras, Resultado } from '../motor';
import { MARCA_NAO_HOMOLOGADA } from './homologacao';
import { leituraNaCota } from './leitura';
import type { LinhaDaLeitura } from './leitura';
import { numeroBr } from './escalas';

/**
 * Uma linha: rótulo, valor (ou «não se aplica»), unidade, o editado, o antes e a marca. O porquê da marca
 * vai numa nota numerada embaixo (`nota`), escrito uma vez só para todas as linhas que o têm. No medido
 * editado o antes vai com o editado — «9,04 kPa · editado: ~~9,04~~ → 4,96 kPa» —, que é o que a conta usa
 * (rodada do agent-browser da 313). O número e a unidade não se separam na quebra de linha (`cptu-sem-quebra`);
 * a classificação, que não tem unidade, quebra onde precisar.
 */
function Linha({ l, nota }: { l: LinhaDaLeitura; nota: number | null }): JSX.Element {
  const unidade = l.unidade ? ` ${l.unidade}` : '';
  const comUnidade = (texto: string) => (unidade ? <span className="cptu-sem-quebra">{texto}{unidade}</span> : texto);
  const antes = l.antes !== null ? <><span className="cptu-antes">{l.antes}</span> → </> : null;
  return (
    <div className={l.marca.homologada ? 'cptu-leitura-linha' : 'cptu-leitura-linha nao-homologada'}>
      <dt>{l.rotulo}</dt>
      <dd>
        {l.naoSeAplica && l.antes === null ? (
          <span className="cptu-nota">{l.naoSeAplica}</span>
        ) : (
          <span className="cptu-valor">
            {l.editado === null ? antes : null}
            {l.naoSeAplica ?? comUnidade(l.texto)}
          </span>
        )}
        {l.editado !== null ? <span className="cptu-nota"> · editado: {antes}{comUnidade(l.editado)}</span> : null}
        {!l.marca.homologada ? ' ' : null}
        {!l.marca.homologada ? (
          <span className="cptu-marca" title={l.marca.motivo ?? undefined}>
            {MARCA_NAO_HOMOLOGADA}{nota !== null ? ` (${nota})` : ''}
          </span>
        ) : null}
      </dd>
    </div>
  );
}

/** A leitura na cota de uma vertical: `ordem` é o índice no BRUTO. */
export function LeituraNaCota(p: {
  bruto: Leituras;
  r: Resultado;
  ordem: number;
  antes?: Resultado | null;
  mostrarNaoHomologadas?: boolean;
}): JSX.Element {
  const mostrarTudo = p.mostrarNaoHomologadas !== false;
  const l = leituraNaCota({ bruto: p.bruto, r: p.r, antes: p.antes ?? null, ordem: p.ordem });
  const grupos = l.grupos
    .map((g) => ({ titulo: g.titulo, linhas: g.linhas.filter((x) => mostrarTudo || x.marca.homologada) }))
    .filter((g) => g.linhas.length > 0);
  // O selo da zona leva a marca do SBTn (as premissas podem tirá-lo da homologação); no Portal, sem ela, some.
  const mostrarZona = l.zona !== null && (mostrarTudo || l.marcaDaZona.homologada);
  // Cada porquê uma vez, numerado na ordem em que aparece (o da zona primeiro).
  const motivos: string[] = [];
  const anotar = (m: { homologada: boolean; motivo: string | null }): void => {
    if (!m.homologada && m.motivo && !motivos.includes(m.motivo)) motivos.push(m.motivo);
  };
  if (mostrarZona) anotar(l.marcaDaZona);
  for (const g of grupos) for (const x of g.linhas) anotar(x.marca);
  const notaDe = (m: { homologada: boolean; motivo: string | null }): number | null =>
    !m.homologada && m.motivo ? motivos.indexOf(m.motivo) + 1 : null;
  return (
    <section className="cptu-leitura">
      <div aria-live="polite">
        <h3 className="cptu-leitura-titulo">
          Na profundidade {numeroBr(l.z, 2)} m
        </h3>
        {mostrarZona && l.zona ? (
          <p className="cptu-selo-da-zona">
            <svg className="cptu-amostra-da-zona" width="14" height="14" aria-hidden="true">
              <rect width="14" height="14" fill={`var(${l.zona.token})`} />
            </svg>
            <span>SBTn {l.zona.numero} · {l.zona.nome}</span>
            {!l.marcaDaZona.homologada ? ' ' : null}
            {!l.marcaDaZona.homologada ? (
              <span className="cptu-marca" title={l.marcaDaZona.motivo ?? undefined}>
                {MARCA_NAO_HOMOLOGADA}{notaDe(l.marcaDaZona) !== null ? ` (${notaDe(l.marcaDaZona)})` : ''}
              </span>
            ) : null}
          </p>
        ) : null}
      </div>
      {l.removida ? <p className="cptu-aviso">{l.removida}</p> : null}
      {l.semAntes ? <p className="cptu-nota">sem leitura antes: o gravado não tem esta leitura</p> : null}
      {grupos.map((g) => (
        <div key={g.titulo} className="cptu-leitura-grupo">
          <h4>{g.titulo}</h4>
          <dl>
            {g.linhas.map((x) => <Linha key={x.coluna} l={x} nota={notaDe(x.marca)} />)}
          </dl>
        </div>
      ))}
      {motivos.length > 0 ? (
        <div className="cptu-leitura-notas">
          {motivos.map((m, k) => (
            <p key={m} className="cptu-nota">
              <span className="cptu-marca">({k + 1})</span> {m}
            </p>
          ))}
        </div>
      ) : null}
    </section>
  );
}
